import type { CompanyCardItem, ResultsCompany } from '@/components/widget/results/results-view.types';
import type { CoberturaDetalle, CotizaTodasResponse } from '@/types/quote.types';
import { buildCoberturasPorPlan, getCoberturaDetalle, normalizeCoverageToken } from './coverage';
import { formatSumaAsegurada } from './formatters';

export interface ComparisonCell {
  suma: string;
  deducible: string;
  sinDeducible: boolean;
}

export interface ComparisonRow {
  key: string;
  nombre: string;
  detalle: string;
  cells: Partial<Record<ResultsCompany, ComparisonCell>>;
}

export interface CompanyQuote {
  company: ResultsCompany;
  quote: CotizaTodasResponse;
}

const isExcluded = (cobertura: CoberturaDetalle): boolean =>
  /excluid/i.test(`${cobertura.SumaAsegurada} ${cobertura.Deducible}`);

const formatSuma = (suma: string, valorVehiculo: number): string =>
  /^v\.?\s*comercial/i.test(suma.trim()) ? 'Valor comercial' : formatSumaAsegurada(suma, valorVehiculo);

const toCell = (cobertura: CoberturaDetalle, valorVehiculo: number): ComparisonCell => ({
  suma: formatSuma(cobertura.SumaAsegurada, valorVehiculo),
  deducible: cobertura.Deducible,
  sinDeducible: cobertura.Deducible?.toUpperCase().includes('SIN') ?? false,
});

/**
 * One row per coverage name across insurers, so their conditions read side by side.
 * Insurers use their own coverage ids, so rows match on the normalized name.
 */
export function buildCoverageComparison(quotes: CompanyQuote[]): ComparisonRow[] {
  const rows = new Map<string, ComparisonRow>();

  for (const { company, quote } of quotes) {
    const valorVehiculo = quote.Cobertura?.ValorVehiculo;
    for (const cobertura of buildCoberturasPorPlan(quote)) {
      const key = normalizeCoverageToken(cobertura.Nombre);
      const row = rows.get(key) ?? { key, nombre: cobertura.Nombre, detalle: getCoberturaDetalle(cobertura.Nombre), cells: {} };
      if (!isExcluded(cobertura)) row.cells[company] = toCell(cobertura, valorVehiculo);
      rows.set(key, row);
    }
  }

  return Array.from(rows.values());
}

export interface ComparisonColumn extends CompanyCardItem {
  available: boolean;
}

const ALWAYS_SHOWN: Array<Pick<CompanyCardItem, 'company' | 'name' | 'logoType'>> = [
  { company: 'QUALITAS', name: 'Quálitas', logoType: 'qualitas' },
  { company: 'ANA', name: 'ANA Seguros', logoType: 'ana' },
];

/**
 * Quálitas and ANA always get a column, so a failed quote reads as "not available"
 * instead of the insurer silently disappearing.
 */
export function buildComparisonColumns(
  cards: CompanyCardItem[],
  logos: Record<'QUALITAS' | 'ANA', string>,
): ComparisonColumn[] {
  const fixed = ALWAYS_SHOWN.map((base): ComparisonColumn => {
    const quoted = cards.find((card) => card.company === base.company);
    if (quoted) return { ...quoted, available: true };
    return {
      ...base,
      logoSrc: logos[base.company as 'QUALITAS' | 'ANA'],
      price: 0,
      firstPayment: 0,
      subsequentPayment: 0,
      subsequentCount: 0,
      isBestPrice: false,
      emittable: false,
      available: false,
    };
  });
  const extra = cards
    .filter((card) => !ALWAYS_SHOWN.some((base) => base.company === card.company))
    .map((card): ComparisonColumn => ({ ...card, available: true }));
  return [...fixed, ...extra];
}
